import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import type { ConfiguratorOriginsType } from '@susu/custom-made/utils/constants';
import { ConfiguratorOrigin, FabricViewMode } from '@susu/custom-made/utils/constants';
import { Section } from '@susu/custom-made/utils/navigation';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { useCallback, useState } from 'react';

const { ENABLE_MODEL_VIEW } = FEATURE_TOGGLES;

/**
 * Single owner of the fabric view mode (AI model image vs layered preview).
 *
 * The mode is derived, never stored: the preferred view, unless a model image error put it in fallback.
 *
 * - Entering a section resets the mode: AI model on a normal open while the customer has only been in the
 *   fabric section, layered everywhere else (including a return to fabric).
 * - The toggle sets the customer's choice for the current section visit; leaving the section forgets it.
 * - A model image error falls back to layered; this is not the customer's choice.
 * - A successful model image load switches back to the AI model when that is the preferred view: the
 *   customer's choice, otherwise the default above (CFS-17007).
 *
 * A ready-to-wear start is already excluded by the origin: product codes make it `CustomizeFlow`.
 */
export function useFabricViewMode({ origin, section }: { origin: ConfiguratorOriginsType; section: string | null }) {
  const isModelViewEnabled = useFeatureIsOn(ENABLE_MODEL_VIEW);
  const [chosenViewMode, setChosenViewMode] = useState<FabricViewMode | null>(null);
  const [hasLeftFabric, setHasLeftFabric] = useState(false);
  const [lastSection, setLastSection] = useState(section);
  const [isFallback, setIsFallback] = useState(false);

  if (section && section !== Section.Fabric && !hasLeftFabric) {
    setHasLeftFabric(true);
  }
  if (section !== lastSection) {
    setLastSection(section);
    setChosenViewMode(null);
    setIsFallback(false);
  }

  const isFabricOnlyVisit =
    isModelViewEnabled && origin === ConfiguratorOrigin.Normal && section === Section.Fabric && !hasLeftFabric;
  const prefersModelView =
    (chosenViewMode ?? (isFabricOnlyVisit ? FabricViewMode.Model : FabricViewMode.Layers)) === FabricViewMode.Model;
  const fabricViewMode =
    isModelViewEnabled && prefersModelView && !isFallback ? FabricViewMode.Model : FabricViewMode.Layers;

  const chooseViewMode = useCallback((viewMode: FabricViewMode) => {
    setChosenViewMode(viewMode);
    setIsFallback(false);
  }, []);

  const onModelImageLoaded = useCallback(() => setIsFallback(false), []);
  const onModelImageError = useCallback(() => setIsFallback(true), []);

  return { fabricViewMode, chooseViewMode, onModelImageLoaded, onModelImageError };
}

/** What the preview shows for a given view mode and model image state. */
export function getModelViewDisplay({
  isModelViewEnabled,
  fabricViewMode,
  section,
  isImageError,
  isImageLoadingOnFirstRender,
}: {
  isModelViewEnabled: boolean;
  fabricViewMode: FabricViewMode;
  section: string | null;
  isImageError: boolean;
  isImageLoadingOnFirstRender: boolean;
}) {
  const isToggleShown = isModelViewEnabled && section === Section.Fabric;

  return {
    // Gated on the section too, so the commit before the section reset never paints the model elsewhere.
    isModelViewActive: isToggleShown && !isImageError && fabricViewMode === FabricViewMode.Model,
    isToggleShown,
    isToggleHidden: isImageError || isImageLoadingOnFirstRender,
  };
}
